import React, {
  useMemo,
  useCallback,
} from "react";

import {
  Box,
  createColumnHelper,
  DataTable,
  Text,
  type DataTableColumnDef,
  type DataTableGroupType,
  type OnChangeFn,
  type SortingState,
} from "@salestrak/ui";
import { IssueViewContext } from "#app/components/issue-view";
import { useTableViewSettings } from "#app/lib/hooks/use-table-view-settings";
import {
  type DashboardDrilldownIssue,
  DashboardIssueDrilldownDocument,
  type DashboardIssueGroupSummary,
} from "#app/lib/data";
import {
  colorDotStyle,
  batterySegmentStyle,
  CHART_PALETTE,
} from "../lib/constants";
import {
  GroupQueryProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import { useDrilldownGroups } from "../hooks/use-drilldown-groups";
import {
  DrilldownGroupTitle,
  DrilldownIssueLabel,
  DrilldownMemberTag,
} from "./DrilldownGroupTitle";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

const BATTERY_BAR_STYLE = { height: 8 } as const;
const GROUP_PAGE_SIZE = 50;

// ── Row type ─────────────────────────────────────────────────────────────────

type StageRow = {
  id: string;
  _issueId: string;
  _createdRaw: string;
  _stageColor: string;
  project: string;
  address: string;
  created: string;
  stage: string;
  workflow: string;
};

const h = createColumnHelper<StageRow>();

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Group title ──────────────────────────────────────────────────────────────

type StageMeta = {
  userId: string;
  name: string;
  count: number;
  stages: { name: string; pct: number; color: string }[];
};

const StageGroupTitle = React.memo(function StageGroupTitle({
  meta,
  onVisible,
}: Omittable<{
  meta: StageMeta;
  onVisible?: () => void;
}>) {
  const visible = meta.stages.filter((s) => s.pct > 0);

  return (
    <DrilldownGroupTitle onVisible={onVisible}>
      <Box direction="column" gap="xs">
        <Box direction="row" gap="xs" align="center">
          <DrilldownMemberTag userId={meta.userId} name={meta.name} />
          <Text size="xs" variant="muted">
            {meta.count} projects
          </Text>
        </Box>
        {visible.length > 0 && (
          <>
            <Box direction="row" gap="sm" wrap>
              {meta.stages.map((s) => (
                <Box direction="row" key={s.name} gap="xs" align="center">
                  <Box
                    width={6}
                    height={6}
                    rounded="full"
                    style={colorDotStyle(s.color)}
                  />
                  <Text size="xs" variant="muted">
                    {s.name} {s.pct}%
                  </Text>
                </Box>
              ))}
            </Box>
            <Box
              direction="row"
              rounded="full"
              overflow="hidden"
              style={BATTERY_BAR_STYLE}
            >
              {visible.map((s) => (
                <Box key={s.name} style={batterySegmentStyle(s.pct, s.color)} />
              ))}
            </Box>
          </>
        )}
      </Box>
    </DrilldownGroupTitle>
  );
});

// ── Props ─────────────────────────────────────────────────────────────────────

export type StageBreakdownQueryInput = Omittable<{
  startDate: string;
  endDate: string;
  teamIds?: string[];
  memberIds?: string[];
  stageName?: string;
  workflowName?: string;
  sortField?: string;
  sortDirection?: string;
}>;

interface StageBreakdownTableProps {
  summaries: DashboardIssueGroupSummary[];
  queryInput: StageBreakdownQueryInput;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onClose: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function StageBreakdownTable({
  summaries,
  queryInput,
  sorting,
  onSortingChange,
  onClose,
}: StageBreakdownTableProps) {
  const issueViewCtx = React.useContext(IssueViewContext);
  const { registerState, getGroupProps } = useGroupStates<DashboardDrilldownIssue>();
  const summaryKeys = useMemo(() => summaries.map((s) => s.userId), [summaries]);
  const { activatedGroupKeys, activateGroup } = useDrilldownGroups(summaryKeys);

  const openIssueFromRow = useCallback(
    (row: StageRow) => {
      onClose();
      if (row._issueId) issueViewCtx?.openIssueById(row._issueId);
    },
    [onClose, issueViewCtx],
  );

  // Build a stable color map for all stages across all summaries
  const stageColorMap = useMemo(() => {
    const map = new Map<string, string>();
    const counts = new Map<string, number>();
    for (const summary of summaries) {
      for (const stage of summary.stages) {
        counts.set(stage.name, (counts.get(stage.name) ?? 0) + stage.count);
      }
    }
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    for (const [i, [name]] of sorted.entries()) {
      map.set(name, at(CHART_PALETTE, i % CHART_PALETTE.length, "palette color"));
    }
    return map;
  }, [summaries]);

  const sort = sorting[0];

  const metaByKey = useMemo(() => {
    const next = new Map<string, StageMeta>();
    for (const summary of summaries) {
      next.set(summary.userId, {
        userId: summary.userId,
        name: summary.name,
        count: summary.totalIssues,
        stages: summary.stages
          .map((s) => ({
            name: s.name,
            pct: Math.round(
              (s.count / Math.max(summary.totalIssues, 1)) * 100,
            ),
            color: stageColorMap.get(s.name) ?? "var(--muted-foreground)",
          }))
          .sort((a, b) => b.pct - a.pct),
      });
    }
    return next;
  }, [summaries, stageColorMap]);


  const getConnection = useCallback(
    (data: any) => data?.dashboardIssueDrilldown,
    [],
  );

  // The drilldown row is a CLIENT-ONLY aggregate, not a server noun: it
  // collapses and re-sorts the page in memory. It is built here, where the
  // row type is declared, never inside the data layer.
  const toRows = useCallback(
    (nodes: readonly DashboardDrilldownIssue[]) => {
      const rows: StageRow[] = nodes.map((issue) => {
        const stageName = issue.stageName ?? "Unknown";
        return {
          id: issue.id,
          _issueId: issue.issueId,
          _createdRaw: issue.creationDate ?? "",
          _stageColor: stageColorMap.get(stageName) ?? "var(--muted-foreground)",
          project: issue.name ?? `#${issue.issueId}`,
          address: issue.address ?? "—",
          created: formatDate(issue.creationDate || null),
          stage: stageName === "Unknown" ? "—" : stageName,
          workflow: issue.workflowName ?? "—",
        };
      });

      if (sort) {
        const { id: sortKey, desc } = sort;
        const dir = desc ? -1 : 1;
        rows.sort((a, b) => {
          let cmp = 0;
          if (sortKey === "created") {
            cmp = a._createdRaw.localeCompare(b._createdRaw);
          } else {
            cmp = String(
              a[sortKey as keyof StageRow] ?? "",
            ).localeCompare(
              String(b[sortKey as keyof StageRow] ?? ""),
            );
          }
          return cmp * dir;
        });
      } else {
        rows.sort((a, b) =>
          b._createdRaw.localeCompare(a._createdRaw),
        );
      }

      return rows;
    },
    [sort, stageColorMap],
  );

  const groups = useMemo<DataTableGroupType<StageRow>[]>(() => {
    return summaries.map((summary) => ({
      key: summary.userId,
      label: summary.name,
      totalCount: summary.totalIssues,
      ...getGroupProps(summary.userId),
      data: toRows(getGroupProps(summary.userId).data),
    }));
  }, [summaries, getGroupProps, toRows]);

  const groupTitle = useCallback(
    (groupKey: string) => {
      const meta = metaByKey.get(groupKey);
      if (!meta) return null;
      return (
        <StageGroupTitle
          meta={meta}
          onVisible={() => activateGroup(groupKey)}
        />
      );
    },
    [activateGroup, metaByKey],
  );

  const tableView = useTableViewSettings("drilldown:stageBreakdown");

  const columns = useMemo<DataTableColumnDef<StageRow>[]>(
    () => [
      h.hoverAction({
        accessorKey: "project",
        header: "Project",
        meta: { minWidth: 180 },
        content: (row) => (
          <DrilldownIssueLabel issueId={row._issueId} name={row.project} />
        ),
        onClickAction: (row) => openIssueFromRow(row),
        actionLabel: (row) => `Open ${row.project}`,
      }),
      h.accessor({
        accessorKey: "stage",
        header: "Stage",
        enableSorting: true,
        meta: { minWidth: 160 },
        cell: ({ row }) => (
          <Box direction="row" gap="xs" align="center">
            <Box
              width={8}
              height={8}
              rounded="full"
              style={colorDotStyle(row.original._stageColor)}
            />
            <Text size="sm" truncate={1}>
              {row.original.stage}
            </Text>
          </Box>
        ),
      }),
      h.accessor({ accessorKey: "workflow", header: "Workflow", enableSorting: true, meta: { minWidth: 160 } }),
      h.accessor({ accessorKey: "address", header: "Address", enableSorting: true, meta: { minWidth: 350 } }),
      h.accessor({ accessorKey: "created", header: "Created", enableSorting: true, meta: { width: 120 } }),
    ],
    [openIssueFromRow],
  );

  return (
    <>
      {summaries
        .filter((s) => activatedGroupKeys.has(s.userId))
        .map((summary) => (
          <GroupQueryProvider<DashboardDrilldownIssue>
            key={summary.userId}
            groupKey={summary.userId}
            document={DashboardIssueDrilldownDocument}
            variables={{
              input: {
                ...queryInput,
                assigneeUserId: summary.userId,
              },
              limit: GROUP_PAGE_SIZE,
            }}
            getConnection={getConnection}
            onStateChange={registerState}
          />
        ))}

      <DataTable
        columns={columns}
        groups={groups}
        groupTitle={groupTitle}
        getRowId={(r) => r.id}
        density="compact"
        fillColumnId="project"
        pinnedColumnIds={["project"]}
        stickyHeader
        hideGroupCount
        paddingX="md"
        sorting={sorting}
        onSortingChange={onSortingChange}
        {...tableView.tableProps}
      />
    </>
  );
}
